import { MapPin, FileCode, Award, Shield } from "lucide-react";

interface LocalSEOHowItWorksProps {
  language: 'es' | 'en';
}

export const LocalSEOHowItWorks = ({ language }: LocalSEOHowItWorksProps) => {
  const content = {
    es: {
      title: "Cómo funciona el SEO Local",
      subtitle: "El SEO local es un conjunto de estrategias diseñadas para posicionar tu negocio en búsquedas geográficamente relevantes.",
      pillars: [
        {
          icon: MapPin,
          title: "Google Business Profile",
          description: "Tu perfil de Google es el eje central del SEO local. Optimizarlo correctamente aumenta tu visibilidad en Google Maps y el Local Pack (los 3 resultados destacados)."
        },
        {
          icon: FileCode,
          title: "SEO On-Page Local",
          description: "Páginas de servicio con keywords locales, Schema markup geográfico, y contenido optimizado para búsquedas con intención local."
        },
        {
          icon: Award,
          title: "Autoridad Local",
          description: "Citaciones NAP consistentes en directorios relevantes, reseñas de clientes, y menciones en medios locales que refuerzan tu presencia."
        },
        {
          icon: Shield,
          title: "Señales de Confianza",
          description: "Reseñas positivas, tiempo de respuesta, información de contacto verificada, y consistencia en todos los canales digitales."
        }
      ]
    },
    en: {
      title: "How Local SEO Works",
      subtitle: "Local SEO is a set of strategies designed to position your business in geographically relevant searches.",
      pillars: [
        {
          icon: MapPin,
          title: "Google Business Profile",
          description: "Your Google profile is the core of local SEO. Optimizing it correctly increases your visibility in Google Maps and the Local Pack (the 3 highlighted results)."
        },
        {
          icon: FileCode,
          title: "Local On-Page SEO",
          description: "Service pages with local keywords, geographic Schema markup, and content optimized for searches with local intent."
        },
        {
          icon: Award,
          title: "Local Authority",
          description: "Consistent NAP citations in relevant directories, customer reviews, and mentions in local media that reinforce your presence."
        },
        {
          icon: Shield,
          title: "Trust Signals",
          description: "Positive reviews, response time, verified contact information, and consistency across all digital channels."
        }
      ]
    }
  };

  const t = content[language];

  return (
    <section className="section-padding bg-background">
      <div className="container-custom">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {t.pillars.map((pillar, index) => {
            const IconComponent = pillar.icon;
            return (
              <div 
                key={index}
                className="bg-secondary/30 rounded-2xl p-6 border border-border hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl bg-brand/10 flex items-center justify-center mb-4">
                  <IconComponent className="w-6 h-6 text-brand" />
                </div>
                <h3 className="text-xl font-semibold mb-3">{pillar.title}</h3>
                <p className="text-muted-foreground">{pillar.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
